import { useState } from 'react';
import { X, Loader2, CheckCircle, Sparkles } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { logger } from '../../services/loggingService';
import toast from 'react-hot-toast';

interface WaitlistModalProps {
  onClose: () => void;
}

export function WaitlistModal({ onClose }: WaitlistModalProps) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [position, setPosition] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    company: '',
    useCase: '',
    websiteUrl: '',
    referralSource: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data, error } = await supabase
        .from('waitlist_entries')
        .insert({
          email: formData.email,
          full_name: formData.fullName,
          company: formData.company || null,
          use_case: formData.useCase || null,
          website_url: formData.websiteUrl || null,
          referral_source: formData.referralSource || null,
        })
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          toast.error('This email is already on the waitlist');
        } else {
          throw error;
        }
        return;
      }

      setSuccess(true);
      setPosition(data.position || null);
      toast.success('Successfully joined the waitlist!');
    } catch (error) {
      logger.error('Error joining waitlist', { error, email: formData.email });
      toast.error('Failed to join waitlist. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className='fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4'>
        <div className='bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl shadow-2xl max-w-md w-full p-8 border border-slate-700'>
          <div className='text-center'>
            <div className='w-16 h-16 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-4'>
              <CheckCircle className='w-10 h-10 text-success' />
            </div>
            <h3 className='text-2xl font-bold text-white mb-2'>
              You're on the list!
            </h3>
            <p className='text-text-secondary mb-6'>
              Thanks for joining the waitlist. We'll send you an invitation
              soon.
            </p>

            {position && (
              <div className='bg-accent-500/20 border border-accent-500/30 rounded-lg p-4 mb-6'>
                <div className='text-sm text-accent-300 mb-1'>Your position</div>
                <div className='text-3xl font-bold text-accent-400'>
                  #{position}
                </div>
              </div>
            )}

            <div className='space-y-3 text-sm text-text-secondary mb-6'>
              <p>✓ Early access to all features</p>
              <p>✓ Priority onboarding support</p>
              <p>✓ Special launch pricing</p>
            </div>

            <button
              onClick={onClose}
              className='btn btn-primary w-full px-6 py-3 font-semibold'
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4'>
      <div className='bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl shadow-2xl max-w-lg w-full border border-slate-700'>
        <div className='flex items-center justify-between p-6 border-b border-slate-700'>
          <div>
            <h3 className='text-2xl font-bold text-white mb-1'>
              Join the Waitlist
            </h3>
            <p className='text-sm text-text-secondary'>
              Get early access to AI Dev Platform
            </p>
          </div>
          <button
            onClick={onClose}
            className='text-text-tertiary hover:text-white transition-colors'
          >
            <X className='w-6 h-6' />
          </button>
        </div>

        <form onSubmit={handleSubmit} className='p-6 space-y-4'>
          <div className='bg-accent-500/10 border border-accent-500/30 rounded-lg p-4 flex items-start gap-3'>
            <Sparkles className='w-5 h-5 text-accent-400 flex-shrink-0 mt-0.5' />
            <div className='text-sm text-accent-300'>
              Join hundreds of developers automating their SDLC with AI agents.
              Early access includes all Pro features.
            </div>
          </div>

          <div>
            <label className='block text-sm font-medium text-text-secondary mb-2'>
              Email Address *
            </label>
            <input
              type='email'
              required
              value={formData.email}
              onChange={e =>
                setFormData({ ...formData, email: e.target.value })
              }
              placeholder='you@company.com'
              className='input-base w-full bg-slate-900 border-slate-700 text-white placeholder-text-tertiary focus:ring-primary'
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-text-secondary mb-2'>
              Full Name *
            </label>
            <input
              type='text'
              required
              value={formData.fullName}
              onChange={e =>
                setFormData({ ...formData, fullName: e.target.value })
              }
              placeholder='John Doe'
              className='input-base w-full bg-slate-900 border-slate-700 text-white placeholder-text-tertiary focus:ring-primary'
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-text-secondary mb-2'>
              Company
            </label>
            <input
              type='text'
              value={formData.company}
              onChange={e =>
                setFormData({ ...formData, company: e.target.value })
              }
              placeholder='Acme Inc.'
              className='input-base w-full bg-slate-900 border-slate-700 text-white placeholder-text-tertiary focus:ring-primary'
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-text-secondary mb-2'>
              What will you use AI Dev Platform for?
            </label>
            <textarea
              value={formData.useCase}
              onChange={e =>
                setFormData({ ...formData, useCase: e.target.value })
              }
              placeholder='Tell us about your use case...'
              rows={3}
              className='input-base w-full bg-slate-900 border-slate-700 text-white placeholder-text-tertiary focus:ring-primary resize-none'
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-text-secondary mb-2'>
              How did you hear about us?
            </label>
            <select
              value={formData.referralSource}
              onChange={e =>
                setFormData({ ...formData, referralSource: e.target.value })
              }
              className='input-base w-full bg-slate-900 border-slate-700 text-white focus:ring-primary'
            >
              <option value=''>Select an option</option>
              <option value='search'>Search Engine</option>
              <option value='social'>Social Media</option>
              <option value='referral'>Friend/Colleague</option>
              <option value='blog'>Blog/Article</option>
              <option value='other'>Other</option>
            </select>
          </div>

          <div className='pt-4'>
            <button
              type='submit'
              disabled={loading}
              className='btn-primary w-full font-semibold disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2'
            >
              {loading ? (
                <>
                  <Loader2 className='w-5 h-5 animate-spin' />
                  Joining...
                </>
              ) : (
                'Join Waitlist'
              )}
            </button>
          </div>

          <p className='text-xs text-text-tertiary text-center'>
            By joining, you agree to receive updates about AI Dev Platform.
            Unsubscribe anytime.
          </p>
        </form>
      </div>
    </div>
  );
}
